import { dataSemHora, ehSaida, paraDataLocal } from '@/lib/extrato';
import type {
  GrupoDia,
  LinhaCaixaPorOrigem,
  LinhaExtrato,
  PeriodoFiltro,
  ResumoOrigem,
} from './types';

// Converte um valor (que a view pode mandar como string) em número.
export function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Dinheiro em reais (já com sinal, se negativo).
export function moeda(v: unknown): string {
  return num(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface ConfigOrigem {
  rotulo: string;
  icone: string;
  bolha: string; // classes do "balão" do ícone
  texto: string; // cor do valor/texto
}

export const ORIGENS: Record<string, ConfigOrigem> = {
  inscricao: {
    rotulo: 'Inscrições',
    icone: 'ri-user-star-line',
    bolha: 'bg-blue-100 text-blue-700',
    texto: 'text-blue-700',
  },
  rifa: {
    rotulo: 'Ação entre amigos',
    icone: 'ri-ticket-2-line',
    bolha: 'bg-purple-100 text-purple-700',
    texto: 'text-purple-700',
  },
  loja: {
    rotulo: 'Loja',
    icone: 'ri-store-2-line',
    bolha: 'bg-orange-100 text-orange-700',
    texto: 'text-orange-700',
  },
  avulsa: {
    rotulo: 'Outras entradas',
    icone: 'ri-hand-coin-line',
    bolha: 'bg-emerald-100 text-emerald-700',
    texto: 'text-emerald-700',
  },
  despesa: {
    rotulo: 'Despesas',
    icone: 'ri-arrow-down-circle-line',
    bolha: 'bg-red-100 text-red-700',
    texto: 'text-red-700',
  },
};

const ORDEM_ORIGENS = ['inscricao', 'rifa', 'loja', 'avulsa', 'despesa'];

export function configOrigem(origem: string): ConfigOrigem {
  return (
    ORIGENS[origem] ?? {
      rotulo: origem,
      icone: 'ri-coins-line',
      bolha: 'bg-secondary-100 text-secondary-700',
      texto: 'text-secondary-700',
    }
  );
}

// Agrega as linhas da view por origem (a loja pode ter entrada + saída),
// aplicando o sinal: saída subtrai.
export function resumirPorOrigem(linhas: LinhaCaixaPorOrigem[]): ResumoOrigem[] {
  const mapa = new Map<string, ResumoOrigem>();
  for (const l of linhas) {
    const cfg = configOrigem(l.origem);
    const sinal = ehSaida(l.tipo) ? -1 : 1;
    const existente = mapa.get(l.origem);
    if (!existente) {
      mapa.set(l.origem, {
        origem: l.origem,
        rotulo: cfg.rotulo,
        icone: cfg.icone,
        lancamentos: num(l.lancamentos),
        bruto: num(l.valor) * sinal,
        taxaGateway: num(l.taxa_gateway),
        taxaPlataforma: num(l.taxa_plataforma),
        liquido: num(l.valor_liquido) * sinal,
        ultimoEm: l.ultimo_em,
      });
    } else {
      existente.lancamentos += num(l.lancamentos);
      existente.bruto += num(l.valor) * sinal;
      existente.taxaGateway += num(l.taxa_gateway);
      existente.taxaPlataforma += num(l.taxa_plataforma);
      existente.liquido += num(l.valor_liquido) * sinal;
      if (l.ultimo_em && (!existente.ultimoEm || l.ultimo_em > existente.ultimoEm)) {
        existente.ultimoEm = l.ultimo_em;
      }
    }
  }
  return Array.from(mapa.values()).sort(
    (a, b) => ORDEM_ORIGENS.indexOf(a.origem) - ORDEM_ORIGENS.indexOf(b.origem),
  );
}

// Saldo do caixa = soma dos líquidos (já com sinal de entrada/saída).
export function saldoDoCaixa(resumo: ResumoOrigem[]): number {
  return resumo.reduce((acc, r) => acc + r.liquido, 0);
}

export function rotuloDia(quando: string): string {
  const d = paraDataLocal(quando);
  const agora = new Date();
  const inicioHoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate()).getTime();
  const inicioD = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((inicioHoje - inicioD) / 86400000);
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
}

export function horaDe(quando: string): string {
  // Datas sem hora (despesa paga em uma data) mostram só a data — nunca "21:00".
  const soData = dataSemHora(quando);
  if (soData) return soData;
  return new Date(quando).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// "há 12 s", "há 3 min", "há 1 h" — dá a sensação de que o tempo real está vivo.
export function tempoDesde(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 5) return 'agora';
  if (s < 60) return `há ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  return `há ${h} h`;
}

// Agrupa as linhas (já ordenadas desc) por dia.
export function agruparPorDia(linhas: LinhaExtrato[]): GrupoDia[] {
  const grupos: GrupoDia[] = [];
  let atual: GrupoDia | null = null;
  for (const l of linhas) {
    const rotulo = rotuloDia(l.quando);
    if (!atual || atual.rotulo !== rotulo) {
      atual = { rotulo, linhas: [] };
      grupos.push(atual);
    }
    atual.linhas.push(l);
  }
  return grupos;
}

export function filtrarPorPeriodo(linhas: LinhaExtrato[], periodo: PeriodoFiltro): LinhaExtrato[] {
  if (periodo === 'todos') return linhas;
  const agora = new Date();
  const inicio = new Date(agora);
  if (periodo === 'hoje') inicio.setHours(0, 0, 0, 0);
  else if (periodo === '7dias') inicio.setDate(agora.getDate() - 7);
  else if (periodo === '30dias') inicio.setDate(agora.getDate() - 30);
  return linhas.filter((l) => new Date(l.quando).getTime() >= inicio.getTime());
}

// Data em YYYY-MM-DD (fuso local) a partir de um timestamp.
export function dataISO(quando: string): string {
  const d = paraDataLocal(quando);
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

export function hojeISO(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

export const FORMAS_LANCAMENTO = [
  { valor: 'dinheiro', rotulo: 'Dinheiro' },
  { valor: 'pix', rotulo: 'Pix' },
  { valor: 'transferencia', rotulo: 'Transferência' },
  { valor: 'outro', rotulo: 'Outro' },
];

export const PERIODOS: { valor: PeriodoFiltro; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Tudo' },
  { valor: 'hoje', rotulo: 'Hoje' },
  { valor: '7dias', rotulo: '7 dias' },
  { valor: '30dias', rotulo: '30 dias' },
];